import { StatusPill, type StatusTone } from "@ozpanel/kit";
import { formatRelative } from "@ozpanel/ui";

// alertState — one alert row's state, and WHY, as the server reports it (server.AlertRows).
//
// "absent" used to be the whole answer for three different situations: the metric does not apply on
// this node, it has not been read yet since the panel started, or every read is failing. The third is
// an alert that CANNOT FIRE while it lasts, and in grey it looked exactly like the first. The server now
// says which (`reason`), with the source's own words (`reason_detail`, `last_error`) — rendered here
// once, for the module page and the alert-levels table alike:
//   not applicable → calm (nothing to watch here), not yet → neutral with WHEN, unmeasured → a warning
//   with the error, not collected / unknown → a warning: nobody knows, and unknown is never fine.

/** One /ozpanel/alerts row — the JSON server.alertRow writes, field for field. */
export interface AlertWire {
  key: string;
  label: string;
  class?: string;
  unit?: string;
  because: string;
  threshold?: number;
  deviation_pct?: number;
  trigger?: "threshold" | "deviation" | "";
  interval_seconds?: number;
  /** absent | stale | live */
  state?: string;
  present?: boolean;
  value?: number;
  at?: string;
  average?: number;
  samples?: number;
  instance?: string;
  overridden?: boolean;
  silenced?: boolean;
  declared_threshold?: number;
  declared_deviation_pct?: number;
  /** An alert read over a choice of averaging windows: the window in force, the choices, and the declared one when overridden. */
  window_seconds?: number;
  windows_seconds?: number[];
  declared_window_seconds?: number;
  /** The level a breach is raised at (info never: a breach is at least warn). */
  severity?: string;
  /** The ENGINE's breach verdict — measured alerts only. */
  breached?: boolean;
  breached_since?: string;
  breach_category?: string;
  /** Why there is no live value: event | not_collected | not_yet | not_applicable | unmeasured | stale | unknown. */
  reason?: string;
  reason_detail?: string;
  /** Present while reads fail — even when the last value is still fresh. */
  last_error?: string;
  failing_reads?: number;
  failing_since?: string;
  unmeasured_raised?: boolean;
  checked_at?: string;
  measure_by?: string;
}

interface Verdict {
  tone: StatusTone;
  label: string;
  /** The server's own words, shown under the row. */
  why?: string;
}

const at = (t?: string) => (t ? new Date(t).toLocaleTimeString() : "");

/** reasonOf: what to say about a row with no live value. Undefined for a live, healthy read. */
export function reasonOf(a: AlertWire): Verdict | undefined {
  switch (a.reason) {
    case "event":
      return { tone: "neutral", label: "raised on event", why: a.reason_detail };
    case "not_applicable":
      return { tone: "neutral", label: "not applicable here", why: a.reason_detail };
    case "not_yet":
      return { tone: "neutral", label: a.measure_by ? `first read by ${at(a.measure_by)}` : "not measured yet", why: a.reason_detail };
    case "unmeasured":
      return {
        tone: "warn",
        label: "cannot be measured",
        why: `${a.last_error ?? a.reason_detail ?? "the read fails"}${a.failing_reads ? ` — ${a.failing_reads} failed read(s)` : ""}${a.failing_since ? `, since ${formatRelative(a.failing_since)}` : ""}. While this lasts the alert cannot fire.`,
      };
    case "not_collected":
      return { tone: "warn", label: "not collected here", why: a.reason_detail };
    case "stale":
      return { tone: "warn", label: "stale", why: a.reason_detail };
    case "unknown":
      return { tone: "warn", label: "unknown", why: a.reason_detail };
  }
  if (!a.present) {
    // An older server sends no reason: say only what is known.
    return { tone: "neutral", label: a.state || "absent" };
  }
  return undefined;
}

/** AlertReading renders the "now" cell: the value with the engine's verdict, or why there is none. */
export function AlertReading({ a, detail = false }: { a: AlertWire; detail?: boolean }) {
  const r = reasonOf(a);
  const failing = a.last_error && a.reason !== "unmeasured" ? (
    <StatusPill tone="warn">last read failed</StatusPill>
  ) : null;
  if (r && !a.present) {
    return (
      <span className="flex flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <StatusPill tone={r.tone}>{r.label}</StatusPill>
        </span>
        {detail && r.why && <span className="text-[11.5px] leading-snug text-muted">{r.why}</span>}
      </span>
    );
  }
  const v = `${a.value}${a.unit ? " " + a.unit : ""}`;
  // The ENGINE's verdict when the server sends it; an older server falls back to the comparison.
  const breached =
    a.breached ??
    !!((a.threshold && (a.value ?? 0) >= a.threshold) ||
      (a.deviation_pct && a.average && (a.value ?? 0) >= a.average * (1 + a.deviation_pct / 100)));
  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex items-center gap-2">
        <span className="font-mono tabular">{v}</span>
        {r && <StatusPill tone={r.tone}>{r.label}</StatusPill>}
        {failing}
        {breached ? <StatusPill tone="bad">over{a.breached_since ? ` since ${at(a.breached_since)}` : ""}</StatusPill> : <StatusPill tone="ok">ok</StatusPill>}
      </span>
      {detail && (r?.why || a.last_error) && (
        <span className="text-[11.5px] leading-snug text-muted">{r?.why ?? `latest read failed: ${a.last_error}`}</span>
      )}
    </span>
  );
}
